import { useState } from "react";
import { Modal, Button, Radio, Space } from "antd";
import SingleInvoiceFlow from "./SingleInvoiceFlow";
import MultiInvoiceFlow from "./MultiInvoiceFlow";
import "./index.css";

interface UploadInvoiceModalProps {
  setIsModalVisible: (visible: boolean) => void;
  isModalVisible: boolean;
  searchParams: { fields: string[]; value: string };
  pageSize: number;
  setIsLoading: (visible: boolean) => void;
  currentPage: number;
}

type ImportType = "single" | "multiple";

const IMPORT_TYPES: Array<{
  value: ImportType;
  title: string;
  description: string;
}> = [
  {
    value: "single",
    title: "One invoice per import",
    description: "Enter invoice details, then upload serials for that invoice.",
  },
  {
    value: "multiple",
    title: "Multiple invoices per import",
    description:
      "Upload customer, invoice, item and serial details in one Excel file.",
  },
];

const UploadInvoiceModal = ({
  isModalVisible,
  setIsModalVisible,
  setIsLoading,
  searchParams,
  pageSize,
  currentPage,
}: UploadInvoiceModalProps) => {
  // selectedType = radio choice; activeType = flow currently shown (null = type selection)
  const [selectedType, setSelectedType] = useState<ImportType>("single");
  const [activeType, setActiveType] = useState<ImportType | null>(null);

  const handleClose = () => {
    setIsModalVisible(false);
    setActiveType(null);
    setSelectedType("single");
  };

  const subtitle =
    activeType === null
      ? "Select the type of data import."
      : activeType === "single"
      ? "One invoice per import. Enter invoice details first, then upload Excel."
      : "Upload serials for multiple customers and invoices in a single Excel file.";

  return (
    <Modal
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            Import Serials from Excel
          </div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
            {subtitle}
          </div>
        </div>
      }
      open={isModalVisible}
      onCancel={handleClose}
      width={800}
      footer={null}
      closable
      centered
      maskClosable={false}
      destroyOnClose
    >
      {activeType === null && (
        <div>
          <Radio.Group
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              {IMPORT_TYPES.map((type) => (
                <div
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  style={{
                    padding: "16px",
                    border:
                      selectedType === type.value
                        ? "2px solid #1890ff"
                        : "1px solid #d9d9d9",
                    borderRadius: "4px",
                    width: "100%",
                    cursor: "pointer",
                  }}
                >
                  <Radio value={type.value}>
                    <span style={{ fontWeight: 500 }}>{type.title}</span>
                  </Radio>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#8c8c8c",
                      marginTop: 4,
                      marginLeft: 22,
                    }}
                  >
                    {type.description}
                  </div>
                </div>
              ))}
            </Space>
          </Radio.Group>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 24,
            }}
          >
            <Space>
              <Button onClick={handleClose} size="large">
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => setActiveType(selectedType)}
              >
                Next
              </Button>
            </Space>
          </div>
        </div>
      )}

      {activeType === "single" && (
        <SingleInvoiceFlow
          isModalVisible={isModalVisible}
          onClose={handleClose}
          onChangeType={() => setActiveType(null)}
          setIsLoading={setIsLoading}
          searchParams={searchParams}
          pageSize={pageSize}
          currentPage={currentPage}
        />
      )}

      {activeType === "multiple" && (
        <MultiInvoiceFlow
          onClose={handleClose}
          onChangeType={() => setActiveType(null)}
          setIsLoading={setIsLoading}
          searchParams={searchParams}
          pageSize={pageSize}
          currentPage={currentPage}
        />
      )}
    </Modal>
  );
};

export default UploadInvoiceModal;
